import React, { FC, useMemo } from 'react';
import {
    Tab, Tabs, TabList, TabPanel,
} from 'react-tabs';
import CN from 'classnames';
import { DesignSet, RecommendDesign } from '@app/react/const/type';
import { initStatePartsSelect } from '@app/react/reducer/PartsSelectReducer';
import { ArrowThumbnail3DContainer } from '@container/thumbnail';
import type {
    FamilyCrestColor,
    RecommendDesignItemsFamilycrestColor,
} from '@app/react/const/type';
import { MAKIWARA_THREAD_MODE_C } from '@app/react/const/variables';

const convertFamilycrestColor = (
    familycrestColor: RecommendDesignItemsFamilycrestColor | null,
): FamilyCrestColor | null => {
    if (familycrestColor === null) {
        return null;
    }

    return {
        id: familycrestColor.id,
        name: 'undefined',
        enable: true,
        productImage: familycrestColor.productImage || '',
        textureImage: familycrestColor.textureImage || '',
    };
};

export interface OrderDesignSelectComponentProps {
    className?: string;
    recommendDesignItems: Array<RecommendDesign>;
    yourDesignItems: DesignSet[];
    onSelectDesign: (item: DesignSet) => void;
    handleNewDesign: (item: DesignSet) => void;
    showDialog: (item: DesignSet) => void;
}

export const OrderDesignSelectComponent: FC<OrderDesignSelectComponentProps> = (
    props,
) => {
    const {
        className,
        recommendDesignItems,
        yourDesignItems,
        onSelectDesign,
        handleNewDesign,
        showDialog,
    } = props;

    const rootClassName = CN('order-design-select', className);

    const recommendThumbnails = useMemo(() => recommendDesignItems.map((i) => (
        <>
            <ArrowThumbnail3DContainer
                key={i.id}
                className="item"
                onSelect={onSelectDesign}
                item={{
                    ...i.items,
                    id: i.id,
                    baseId: null,
                    threadGloss: false,
                    threadMode: MAKIWARA_THREAD_MODE_C,
                    carvingString: i.items.carvingString || null,
                    familycrestColor: convertFamilycrestColor(
                        i.items.familycrestColor,
                    ),
                }}
            />
            <p className="arrow-thumbnail-3d__title">
                {i.name}
            </p>
            <p className="arrow-thumbnail-3d__detail-text">
                選択中のシャフト:
                {i.items.shaft?.categoryName}
                {i.items.shaft?.subCategoryName}
                {i.items.shaft?.name}
            </p>
        </>
    )), [recommendDesignItems, onSelectDesign]);

    const yourDesignThumbnails = useMemo(() => {
        if (yourDesignItems.length === 0) {
            return null;
        }

        const items = yourDesignItems.map((i, index) => (
            <>
                <ArrowThumbnail3DContainer
                    key={i.id}
                    className="item"
                    item={i}
                    availableDelete
                    availableInCart
                    onSelect={onSelectDesign}
                    onDelete={() => showDialog(i)}
                />
                <p className="arrow-thumbnail-3d__title">
                    あなたの矢
                    {index + 1}
                </p>
                <p className="arrow-thumbnail-3d__detail-text">
                    選択中のシャフト:
                    {i.shaft?.categoryName}
                    {i.shaft?.subCategoryName}
                    {i.shaft?.name}
                </p>
            </>
        ));

        return items;
    }, [yourDesignItems, onSelectDesign, showDialog]);

    return (
        <div className={rootClassName}>
            <Tabs forceRenderTabPanel>
                <TabList>
                    {yourDesignItems.length !== 0 && (
                        <Tab>あなたのデザイン</Tab>
                    )}
                    <Tab>新規デザイン</Tab>
                </TabList>

                {yourDesignItems.length !== 0 && (
                    <TabPanel>
                        <ul className="order-design-select-container">
                            <p className="order-design-select-container__comment">
                                デザインを選んでカスタマイズ
                                <br />
                                完了したらカートに追加
                            </p>
                            {yourDesignThumbnails}
                        </ul>
                    </TabPanel>
                )}
                <TabPanel>
                    <ul className="order-design-select-container">
                        <p className="order-design-select-container__comment">
                            オーダーメイドのベースになる
                            <br />
                            デザインを選んでください
                        </p>
                        <>
                            <ArrowThumbnail3DContainer
                                className="item"
                                onSelect={handleNewDesign}
                                item={{
                                    ...initStatePartsSelect,
                                    id: -9999,
                                }}
                            />
                            <p className="arrow-thumbnail-3d__title">
                                プレーンデザイン
                            </p>
                            <p className="arrow-thumbnail-3d__detail-text">
                                0からデザインを始めたい方
                            </p>
                        </>
                        {recommendThumbnails}
                    </ul>
                </TabPanel>
            </Tabs>
        </div>
    );
};
